import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import {
  ArrowUpRight, Check, FileText, Clock, ShieldCheck, Sparkles,
  Calendar, ClipboardCheck, Send,
} from "lucide-react";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
};

const pageI18n = {
  de: {
    viewPricing: "Preise ansehen",
    trust: ["Fixpreis, keine Überraschungen", "Digitale Einreichung inklusive", "Alle Kantone der Schweiz"],
    pricingEyebrow: "Fixpreise",
    pricingHeading: ["Klar, fair, ohne", "Überraschung", "."],
    processHeadingPre: "In",
    processHeadingAccent: "3 Schritten",
    processHeadingPost: "zur fertigen Erklärung.",
    includedEyebrow: "Leistungsumfang",
    includedHeadingPre: "Alles inklusive. Keine",
    includedHeadingAccent: "Nachforderungen",
    includedFeatures: [
      "Persönliche Aufnahme Ihrer Situation",
      "Vollständiges Ausfüllen der Steuererklärung",
      "Optimierung sämtlicher Abzüge",
      "Digitale Einreichung beim Steueramt",
      "Ansprechpartner bei Rückfragen der Steuerbehörde",
      "Fristverlängerung auf Wunsch",
    ],
    imgCaption: "Alle Kantone der Schweiz",
    imgSubcaption: "Rechtssicher & digital",
    finalBadge: "Bereit für Ihre Steuererklärung?",
    finalDescription: "Kontaktieren Sie uns – wir melden uns innerhalb von 24 Stunden mit einem persönlichen Terminvorschlag.",
  },
  en: {
    viewPricing: "See pricing",
    trust: ["Fixed price, no surprises", "Digital submission included", "All Swiss cantons"],
    pricingEyebrow: "Fixed prices",
    pricingHeading: ["Clear, fair, no", "surprises", "."],
    processHeadingPre: "In",
    processHeadingAccent: "3 steps",
    processHeadingPost: "to your finished return.",
    includedEyebrow: "What's included",
    includedHeadingPre: "Everything included. No",
    includedHeadingAccent: "extra charges",
    includedFeatures: [
      "Personal review of your situation",
      "Complete filing of your tax return",
      "Optimisation of every deduction",
      "Digital submission to the tax office",
      "Point of contact for tax authority queries",
      "Deadline extension on request",
    ],
    imgCaption: "All Swiss cantons",
    imgSubcaption: "Legally sound & digital",
    finalBadge: "Ready for your tax return?",
    finalDescription: "Contact us – we'll get back to you within 24 hours with a personal appointment proposal.",
  },
  fr: {
    viewPricing: "Voir les prix",
    trust: ["Prix fixe, sans surprise", "Soumission numérique incluse", "Tous les cantons suisses"],
    pricingEyebrow: "Prix fixes",
    pricingHeading: ["Clair, équitable, sans", "surprise", "."],
    processHeadingPre: "En",
    processHeadingAccent: "3 étapes",
    processHeadingPost: "vers votre déclaration finie.",
    includedEyebrow: "Prestations incluses",
    includedHeadingPre: "Tout inclus. Aucun",
    includedHeadingAccent: "supplément",
    includedFeatures: [
      "Analyse personnelle de votre situation",
      "Remplissage complet de la déclaration",
      "Optimisation de toutes les déductions",
      "Soumission numérique à l'administration fiscale",
      "Interlocuteur en cas de questions du fisc",
      "Prolongation de délai sur demande",
    ],
    imgCaption: "Tous les cantons suisses",
    imgSubcaption: "Sûr juridiquement & numérique",
    finalBadge: "Prêt pour votre déclaration ?",
    finalDescription: "Contactez-nous – nous vous répondrons sous 24 heures avec une proposition de rendez-vous personnelle.",
  },
  it: {
    viewPricing: "Vedi i prezzi",
    trust: ["Prezzo fisso, nessuna sorpresa", "Trasmissione digitale inclusa", "Tutti i cantoni svizzeri"],
    pricingEyebrow: "Prezzi fissi",
    pricingHeading: ["Chiaro, equo, senza", "sorprese", "."],
    processHeadingPre: "In",
    processHeadingAccent: "3 passi",
    processHeadingPost: "alla dichiarazione pronta.",
    includedEyebrow: "Cosa è incluso",
    includedHeadingPre: "Tutto incluso. Nessun",
    includedHeadingAccent: "sovrapprezzo",
    includedFeatures: [
      "Analisi personale della vostra situazione",
      "Compilazione completa della dichiarazione",
      "Ottimizzazione di ogni detrazione",
      "Trasmissione digitale all'ufficio imposte",
      "Referente in caso di domande del fisco",
      "Proroga della scadenza su richiesta",
    ],
    imgCaption: "Tutti i cantoni svizzeri",
    imgSubcaption: "Legalmente sicuro & digitale",
    finalBadge: "Pronto per la vostra dichiarazione?",
    finalDescription: "Contattateci – vi risponderemo entro 24 ore con una proposta di appuntamento personale.",
  },
  ta: {
    viewPricing: "விலைகள் காண",
    trust: ["நிலையான விலை, ஆச்சரியம் இல்லை", "டிஜிட்டல் சமர்ப்பிப்பு உட்பட", "அனைத்து சுவிஸ் மாநிலங்கள்"],
    pricingEyebrow: "நிலையான விலைகள்",
    pricingHeading: ["தெளிவு, நேர்மை, எந்த", "ஆச்சரியமும்", "இல்லை."],
    processHeadingPre: "",
    processHeadingAccent: "3 படிகளில்",
    processHeadingPost: "முடிக்கப்பட்ட அறிக்கை.",
    includedEyebrow: "சேர்க்கப்பட்டவை",
    includedHeadingPre: "அனைத்தும் உட்பட. எந்த",
    includedHeadingAccent: "கூடுதல் கட்டணமும்",
    includedFeatures: [
      "உங்கள் சூழல் தனிப்பட்ட பகுப்பாய்வு",
      "வரி அறிக்கை முழுமையான நிரப்புதல்",
      "அனைத்து கழிவுகளின் மேம்படுத்தல்",
      "வரி அலுவலகத்திற்கு டிஜிட்டல் சமர்ப்பிப்பு",
      "வரி அதிகாரிகளின் கேள்விகளுக்கு தொடர்பு",
      "காலநீட்டிப்பு கோரிக்கை மேல்",
    ],
    imgCaption: "அனைத்து சுவிஸ் மாநிலங்கள்",
    imgSubcaption: "சட்டப்பூர்வ & டிஜிட்டல்",
    finalBadge: "உங்கள் வரி அறிக்கைக்கு தயாரா?",
    finalDescription: "தொடர்பு கொள்ளுங்கள் – 24 மணி நேரத்திற்குள் தனிப்பட்ட சந்திப்பு பரிந்துரையுடன் நாங்கள் திரும்ப தொடர்பு கொள்வோம்.",
  },
};

const processSteps = {
  de: [
    { icon: Send, t: "Unterlagen einreichen", d: "Sie senden uns Ihre Belege bequem per E-Mail oder bringen sie im Büro vorbei." },
    { icon: ClipboardCheck, t: "Wir prüfen & optimieren", d: "Wir füllen die Steuererklärung aus und holen alle Abzüge für Sie heraus." },
    { icon: Calendar, t: "Digital eingereicht", d: "Nach Ihrer Freigabe reichen wir die Erklärung digital beim Steueramt ein." },
  ],
  en: [
    { icon: Send, t: "Submit your documents", d: "Send us your receipts via email or drop them at the office – whichever is easier." },
    { icon: ClipboardCheck, t: "We review & optimise", d: "We complete your return and secure every deduction you're entitled to." },
    { icon: Calendar, t: "Filed digitally", d: "Once you approve, we submit the return electronically to the tax office." },
  ],
  fr: [
    { icon: Send, t: "Envoyez vos documents", d: "Envoyez-nous vos justificatifs par e-mail ou déposez-les à notre bureau." },
    { icon: ClipboardCheck, t: "Nous vérifions & optimisons", d: "Nous remplissons votre déclaration et maximisons toutes vos déductions." },
    { icon: Calendar, t: "Soumis numériquement", d: "Après votre validation, nous transmettons la déclaration à l'administration fiscale." },
  ],
  it: [
    { icon: Send, t: "Inviate i documenti", d: "Inviateci le ricevute via e-mail o portatele in ufficio – come preferite." },
    { icon: ClipboardCheck, t: "Verifichiamo & ottimizziamo", d: "Compiliamo la dichiarazione e sfruttiamo tutte le detrazioni possibili." },
    { icon: Calendar, t: "Trasmesso digitalmente", d: "Dopo la vostra approvazione, inoltriamo la dichiarazione all'ufficio delle imposte." },
  ],
  ta: [
    { icon: Send, t: "ஆவணங்களை சமர்ப்பிக்கவும்", d: "உங்கள் ரசீதுகளை மின்னஞ்சல் மூலம் அனுப்பவும் அல்லது அலுவலகத்தில் கொண்டு வரவும்." },
    { icon: ClipboardCheck, t: "நாங்கள் சரிபார்க்கிறோம்", d: "வரி அறிக்கையை நிரப்பி, அனைத்து கழிவுகளையும் பெறுகிறோம்." },
    { icon: Calendar, t: "டிஜிட்டல் சமர்ப்பிப்பு", d: "உங்கள் அனுமதிக்குப் பிறகு, வரி அலுவலகத்தில் டிஜிட்டல் முறையில் சமர்ப்பிக்கிறோம்." },
  ],
};

const faqData = {
  de: [
    { q: "Welche Unterlagen brauche ich?", a: "Lohnausweis, Bankauszüge per 31.12., Krankenkassen-Prämien, Versicherungsprämien, Belege für Aus- und Weiterbildung, Spenden und – falls zutreffend – Hypothekar-Bescheinigung und Unterhaltskosten." },
    { q: "Wie schnell erhalte ich meine Steuererklärung zurück?", a: "In der Regel innerhalb von 5–7 Werktagen nach Erhalt aller Unterlagen. Zur Deadline-Zeit kann es etwas länger dauern – bitte frühzeitig kontaktieren." },
    { q: "Kann ich Fristverlängerung beantragen?", a: "Ja, wir übernehmen für Sie die Fristverlängerung bis meist Ende September. Für kantonale Sonderfälle klären wir dies vorgängig ab." },
    { q: "Gibt es versteckte Kosten?", a: "Nein. Die Preise 79.–/99.–/119.– sind Fixpreise. Nur bei sehr komplexen Situationen (mehrere Immobilien, Wertschriftenportfolio) machen wir vorab ein transparentes Angebot." },
  ],
  en: [
    { q: "Which documents do I need?", a: "Wage certificate, bank statements as of Dec 31st, health insurance premiums, insurance premiums, receipts for education, donations and – if applicable – mortgage statement and maintenance costs." },
    { q: "How fast will I get my return?", a: "Usually within 5–7 business days after we receive all documents. During deadline season it can take slightly longer – please contact us early." },
    { q: "Can I request a deadline extension?", a: "Yes, we file the extension for you, usually until end of September. For cantonal special cases we clarify this in advance." },
    { q: "Any hidden costs?", a: "No. The prices 79.–/99.–/119.– are fixed. For very complex situations (multiple properties, securities portfolio) we provide a transparent quote upfront." },
  ],
  fr: [
    { q: "Quels documents dois-je fournir ?", a: "Certificat de salaire, relevés bancaires au 31.12, primes d'assurance maladie, primes d'assurance, justificatifs de formation, dons et – le cas échéant – attestation hypothécaire et frais d'entretien." },
    { q: "Quand recevrai-je ma déclaration ?", a: "Généralement dans les 5 à 7 jours ouvrables après réception de tous les documents. En période de deadline, cela peut prendre un peu plus longtemps." },
    { q: "Puis-je demander une prolongation de délai ?", a: "Oui, nous demandons la prolongation pour vous, généralement jusqu'à fin septembre." },
    { q: "Y a-t-il des frais cachés ?", a: "Non. Les prix 79.–/99.–/119.– sont fixes. Pour les situations complexes, nous établissons un devis transparent au préalable." },
  ],
  it: [
    { q: "Quali documenti servono?", a: "Certificato di salario, estratti conto al 31.12, premi cassa malati, premi assicurativi, ricevute per formazione, donazioni e – se applicabile – attestazione ipotecaria e spese di manutenzione." },
    { q: "In quanto tempo ricevo la dichiarazione?", a: "Di solito entro 5–7 giorni lavorativi dopo aver ricevuto tutti i documenti. Vicino alla scadenza può richiedere un po' più di tempo." },
    { q: "Posso richiedere una proroga?", a: "Sì, richiediamo la proroga per voi, di solito fino a fine settembre." },
    { q: "Ci sono costi nascosti?", a: "No. I prezzi 79.–/99.–/119.– sono fissi. Per situazioni molto complesse forniamo un preventivo trasparente in anticipo." },
  ],
  ta: [
    { q: "எந்த ஆவணங்கள் தேவை?", a: "ஊதிய சான்றிதழ், 31.12 வங்கி அறிக்கைகள், சுகாதார காப்பீட்டு பிரீமியங்கள், காப்பீட்டு பிரீமியங்கள், கல்வி ரசீதுகள், நன்கொடைகள் மற்றும் – பொருந்தினால் – அடமான சான்றிதழ் மற்றும் பராமரிப்பு செலவுகள்." },
    { q: "எவ்வளவு விரைவில் அறிக்கை கிடைக்கும்?", a: "பொதுவாக அனைத்து ஆவணங்களையும் பெற்ற பிறகு 5–7 வேலை நாட்களில்." },
    { q: "காலநீட்டிப்பு கோர முடியுமா?", a: "ஆம், நாங்கள் உங்களுக்காக காலநீட்டிப்பை கோருகிறோம், பொதுவாக செப்டம்பர் இறுதி வரை." },
    { q: "மறைவான கட்டணங்கள் உள்ளதா?", a: "இல்லை. 79.–/99.–/119.– விலைகள் நிலையானவை. மிகவும் சிக்கலான சூழ்நிலைகளுக்கு முன்கூட்டியே வெளிப்படையான மேற்கோள் வழங்குகிறோம்." },
  ],
};

const faqTitleI18n = {
  de: "Häufige Fragen",
  en: "Frequently asked",
  fr: "Questions fréquentes",
  it: "Domande frequenti",
  ta: "அடிக்கடி கேட்கப்படும்",
};

const processTitleI18n = {
  de: "So einfach geht's",
  en: "How it works",
  fr: "Comment ça marche",
  it: "Come funziona",
  ta: "இது எப்படி வேலை செய்கிறது",
};

export default function Steuer() {
  const { t, lang } = useLang();
  const process = processSteps[lang] || processSteps.de;
  const faq = faqData[lang] || faqData.de;
  const faqTitle = faqTitleI18n[lang] || faqTitleI18n.de;
  const processTitle = processTitleI18n[lang] || processTitleI18n.de;
  const p = pageI18n[lang] || pageI18n.de;

  return (
    <div data-testid="page-steuer" className="bg-white text-[#0A0A0A]">
      {/* HERO */}
      <section className="relative pt-20 pb-16 md:pt-28 md:pb-20 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-[700px] h-[700px] bg-[#EA6A1A]/8 rounded-full blur-[120px] pointer-events-none" />
        <div className="absolute -top-20 -left-40 w-[500px] h-[500px] bg-[#EA6A1A]/5 rounded-full blur-[100px] pointer-events-none" />

        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="text-center max-w-4xl mx-auto"
          >
            <div className="inline-flex items-center gap-2 bg-[#0A0A0A] text-white px-4 py-2 rounded-full text-xs font-medium mb-8">
              <FileText size={12} strokeWidth={2} />
              {t.tax.eyebrow}
            </div>

            <h1 className="font-display text-5xl md:text-7xl lg:text-8xl leading-[0.96] tracking-[-0.03em] font-medium">
              {t.tax.title.split(" ").slice(0, -1).join(" ")}{" "}
              <span className="italic text-[#EA6A1A] font-normal">{t.tax.title.split(" ").slice(-1)}</span>
              <span className="text-[#EA6A1A]">.</span>
            </h1>

            <p className="mt-8 text-lg md:text-xl text-[#52525B] max-w-2xl mx-auto leading-[1.6]">
              {t.tax.subtitle}
            </p>

            <div className="mt-10 flex flex-wrap justify-center items-center gap-3">
              <a
                href="#pricing"
                data-testid="steuer-hero-cta-primary"
                className="group inline-flex items-center gap-2 bg-[#0A0A0A] hover:bg-[#EA6A1A] text-white px-6 py-3.5 rounded-full transition-all shadow-sm"
              >
                <span className="text-sm font-medium">{p.viewPricing}</span>
                <ArrowUpRight size={16} className="group-hover:rotate-45 transition-transform" />
              </a>
              <Link
                to="/contact"
                data-testid="steuer-hero-cta-secondary"
                className="inline-flex items-center gap-2 border border-[#E9E9E7] hover:border-[#0A0A0A] text-[#0A0A0A] px-6 py-3.5 rounded-full transition-all"
              >
                <span className="text-sm font-medium">{t.tax.cta}</span>
              </Link>
            </div>

            <div className="mt-12 flex flex-wrap justify-center items-center gap-x-8 gap-y-3 text-sm text-[#71717A]">
              {p.trust.map((label, i) => (
                <div key={i} className="inline-flex items-center gap-2">
                  <Check size={14} className="text-[#EA6A1A]" strokeWidth={2.5} />
                  <span>{label}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="py-20 md:py-28 bg-[#F7F5F0] border-y border-[#E9E9E7] scroll-mt-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-12">
          <motion.div {...fadeUp} className="text-center mb-16 max-w-2xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-white border border-[#E9E9E7] px-3 py-1.5 rounded-full text-xs font-medium mb-5 text-[#52525B]">
              — {p.pricingEyebrow}
            </div>
            <h2 className="font-display text-4xl md:text-5xl leading-[1.05] tracking-[-0.02em] font-medium">
              {p.pricingHeading[0]} <span className="italic text-[#EA6A1A]">{p.pricingHeading[1]}</span>{p.pricingHeading[2]}
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-5 max-w-6xl mx-auto">
            {t.tax.tiers.map((tier, i) => (
              <motion.div
                key={tier.name}
                {...fadeUp}
                transition={{ ...fadeUp.transition, delay: i * 0.08 }}
                data-testid={`steuer-tier-${i}`}
                className={`relative flex flex-col rounded-3xl p-8 md:p-10 transition-all duration-300 ${
                  tier.featured
                    ? "bg-[#0A0A0A] text-white border-2 border-[#EA6A1A] md:-mt-4 md:mb-4 shadow-[0_24px_60px_-12px_rgba(234,106,26,0.35)]"
                    : "bg-white border border-[#E9E9E7] card-shadow hover:-translate-y-1"
                }`}
              >
                {tier.featured && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#EA6A1A] text-white text-xs font-medium px-4 py-1.5 rounded-full">
                    {t.tax.badge}
                  </div>
                )}

                <div className="mb-6">
                  <h3 className={`font-display text-2xl md:text-3xl font-medium tracking-tight mb-2 ${tier.featured ? "text-white" : "text-[#0A0A0A]"}`}>
                    {tier.name}
                  </h3>
                  <p className={`text-sm leading-relaxed ${tier.featured ? "text-white/70" : "text-[#71717A]"}`}>
                    {tier.desc}
                  </p>
                </div>

                <div className="flex items-baseline gap-2 mb-8 pb-8 border-b border-current/10">
                  <span className={`text-xs uppercase tracking-wider ${tier.featured ? "text-white/60" : "text-[#71717A]"}`}>
                    {t.tax.currency}
                  </span>
                  <span className="font-display text-6xl md:text-7xl font-semibold tracking-tighter">
                    {tier.price}
                  </span>
                  <span className={`text-lg ${tier.featured ? "text-white/60" : "text-[#71717A]"}`}>.–</span>
                </div>

                <ul className="space-y-3 mb-8 flex-1">
                  {tier.features.map((f, j) => (
                    <li key={j} className="flex items-start gap-3 text-[15px]">
                      <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                        tier.featured ? "bg-[#EA6A1A]" : "bg-[#EA6A1A]/10"
                      }`}>
                        <Check size={12} className={tier.featured ? "text-white" : "text-[#EA6A1A]"} strokeWidth={2.5} />
                      </div>
                      <span className={tier.featured ? "text-white/90" : "text-[#0A0A0A]"}>{f}</span>
                    </li>
                  ))}
                </ul>

                <Link
                  to="/contact"
                  data-testid={`steuer-tier-cta-${i}`}
                  className={`group inline-flex items-center justify-between gap-2 rounded-full px-6 py-3.5 text-sm font-medium transition-all ${
                    tier.featured
                      ? "bg-[#EA6A1A] text-white hover:bg-white hover:text-[#0A0A0A]"
                      : "bg-[#0A0A0A] text-white hover:bg-[#EA6A1A]"
                  }`}
                >
                  <span>{t.tax.cta}</span>
                  <ArrowUpRight size={16} className="group-hover:rotate-45 transition-transform" />
                </Link>
              </motion.div>
            ))}
          </div>

          <p className="text-center text-xs text-[#71717A] mt-10">{t.tax.note}</p>
        </div>
      </section>

      {/* PROCESS */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-12">
          <motion.div {...fadeUp} className="text-center mb-16 max-w-2xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-[#F5F5F4] border border-[#E9E9E7] px-3 py-1.5 rounded-full text-xs font-medium mb-5 text-[#52525B]">
              — {processTitle}
            </div>
            <h2 className="font-display text-4xl md:text-5xl leading-[1.05] tracking-[-0.02em] font-medium">
              {p.processHeadingPre} <span className="italic text-[#EA6A1A]">{p.processHeadingAccent}</span> {p.processHeadingPost}
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {process.map((step, i) => {
              const Icon = step.icon;
              return (
                <motion.div
                  key={i}
                  {...fadeUp}
                  transition={{ ...fadeUp.transition, delay: i * 0.1 }}
                  data-testid={`steuer-step-${i}`}
                  className="relative bg-white border border-[#E9E9E7] rounded-2xl p-8 card-shadow"
                >
                  <div className="flex items-center justify-between mb-8">
                    <div className="w-12 h-12 bg-[#F5F5F4] rounded-xl flex items-center justify-center">
                      <Icon className="text-[#0A0A0A]" size={22} strokeWidth={1.6} />
                    </div>
                    <span className="font-display text-3xl text-[#EA6A1A] font-semibold">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                  </div>
                  <h3 className="font-display text-xl font-medium mb-3 tracking-tight">{step.t}</h3>
                  <p className="text-[#71717A] leading-relaxed text-[15px]">{step.d}</p>

                  {i < process.length - 1 && (
                    <div className="hidden md:block absolute top-1/2 -right-3 w-6 h-px bg-[#E9E9E7]" />
                  )}
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHAT'S INCLUDED */}
      <section className="py-24 md:py-32 bg-[#F7F5F0] border-y border-[#E9E9E7]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-12 grid md:grid-cols-12 gap-10 md:gap-16 items-center">
          <motion.div {...fadeUp} className="md:col-span-6">
            <div className="relative rounded-3xl overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1554224155-6726b3ff858f?crop=entropy&cs=srgb&fm=jpg&fit=crop&w=1200&h=1000&q=85"
                alt="Steuererklärung"
                className="w-full h-[500px] object-cover"
              />
              <div className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur-md rounded-2xl p-5 flex items-center gap-4 card-shadow">
                <div className="w-12 h-12 bg-[#EA6A1A] rounded-full flex items-center justify-center flex-shrink-0">
                  <ShieldCheck className="text-white" size={20} strokeWidth={2} />
                </div>
                <div>
                  <div className="font-medium text-[15px]">{p.imgCaption}</div>
                  <div className="text-xs text-[#71717A]">{p.imgSubcaption}</div>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.1 }} className="md:col-span-6">
            <div className="inline-flex items-center gap-2 bg-white border border-[#E9E9E7] px-3 py-1.5 rounded-full text-xs font-medium mb-6 text-[#52525B]">
              — {p.includedEyebrow}
            </div>
            <h2 className="font-display text-4xl md:text-5xl leading-[1.05] tracking-[-0.02em] font-medium mb-8">
              {p.includedHeadingPre} <span className="italic text-[#EA6A1A]">{p.includedHeadingAccent}</span>.
            </h2>
            <ul className="space-y-3">
              {p.includedFeatures.map((f, i) => (
                <li key={i} className="flex items-start gap-3 text-[15px]">
                  <div className="w-6 h-6 rounded-full bg-[#EA6A1A]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check size={14} className="text-[#EA6A1A]" strokeWidth={2.5} />
                  </div>
                  <span className="text-[#0A0A0A]">{f}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-12">
          <motion.div {...fadeUp} className="text-center mb-16 max-w-2xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-[#F5F5F4] border border-[#E9E9E7] px-3 py-1.5 rounded-full text-xs font-medium mb-5 text-[#52525B]">
              — FAQ
            </div>
            <h2 className="font-display text-4xl md:text-5xl leading-[1.05] tracking-[-0.02em] font-medium">
              {faqTitle}
            </h2>
          </motion.div>

          <div className="max-w-3xl mx-auto space-y-3">
            {faq.map((item, i) => (
              <motion.details
                key={i}
                {...fadeUp}
                transition={{ ...fadeUp.transition, delay: i * 0.05 }}
                data-testid={`steuer-faq-${i}`}
                className="group bg-white border border-[#E9E9E7] rounded-2xl overflow-hidden card-shadow"
              >
                <summary className="flex items-center justify-between p-6 md:p-7 cursor-pointer list-none">
                  <h3 className="font-medium text-lg text-[#0A0A0A] pr-6">{item.q}</h3>
                  <div className="w-8 h-8 bg-[#F5F5F4] group-open:bg-[#EA6A1A] rounded-full flex items-center justify-center transition-colors flex-shrink-0">
                    <span className="text-[#0A0A0A] group-open:text-white text-lg leading-none transition-colors">
                      <span className="group-open:hidden">+</span>
                      <span className="hidden group-open:inline">−</span>
                    </span>
                  </div>
                </summary>
                <div className="px-6 md:px-7 pb-6 md:pb-7 text-[#71717A] leading-[1.7]">
                  {item.a}
                </div>
              </motion.details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="pb-24 md:pb-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-12">
          <motion.div
            {...fadeUp}
            className="relative overflow-hidden bg-[#0A0A0A] rounded-3xl p-10 md:p-20 text-center"
          >
            <div className="absolute -top-20 -right-20 w-96 h-96 bg-[#EA6A1A]/30 rounded-full blur-[100px] pointer-events-none" />
            <div className="absolute -bottom-20 -left-20 w-96 h-96 bg-[#EA6A1A]/20 rounded-full blur-[100px] pointer-events-none" />
            <div className="relative">
              <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 backdrop-blur px-3 py-1.5 rounded-full text-xs font-medium text-white/90 mb-6">
                <Sparkles size={12} /> {p.finalBadge}
              </div>
              <h2 className="font-display text-5xl md:text-7xl leading-[0.98] tracking-[-0.03em] text-white font-medium max-w-4xl mx-auto mb-6">
                {t.tax.currency} <span className="italic text-[#EA6A1A]">{t.tax.tiers[0].price}.–</span>
              </h2>
              <p className="text-white/70 text-lg max-w-2xl mx-auto mb-10">
                {p.finalDescription}
              </p>
              <Link
                to="/contact"
                data-testid="steuer-cta-final"
                className="group inline-flex items-center gap-2 bg-white text-[#0A0A0A] hover:bg-[#EA6A1A] hover:text-white px-7 py-4 rounded-full transition-all"
              >
                <span className="text-sm font-medium">{t.tax.cta}</span>
                <ArrowUpRight size={16} className="group-hover:rotate-45 transition-transform" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
